import { auth, db, functions } from "@/config/firebase";
import { AuthService } from "./authService";
import { deleteUser } from "firebase/auth";
import { doc, deleteDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

export class AdminService {
  // Check if current user is an admin
  static async isAdmin() {
    const user = auth.currentUser;
    if (!user) return false;

    try {
      const getAdminEmails = httpsCallable(functions, "getAdminEmails");
      const result = await getAdminEmails();
      if (result.data.success) {
        return result.data.adminEmails.includes(user.email);
      }
      return false;
    } catch (error) {
      console.error("Error checking admin status:", error);
      return false;
    }
  }

  // Check if user is admin by email
  static async isAdminEmail(email) {
    try {
      const getAdminEmails = httpsCallable(functions, "getAdminEmails");
      const result = await getAdminEmails();
      if (result.data.success) {
        return result.data.adminEmails.includes(email);
      }
      return false;
    } catch (error) {
      console.error("Error checking admin email:", error);
      return false;
    }
  }

  // List all survey users from Firestore
  static async listSurveyUsers() {
    try {
      const { collection, getDocs, query, orderBy } = await import(
        "firebase/firestore"
      );
      const usersRef = collection(db, "users");
      const q = query(usersRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);

      const users = [];
      querySnapshot.forEach((doc) => {
        users.push({
          uid: doc.id,
          ...doc.data(),
        });
      });

      return { success: true, users };
    } catch (error) {
      console.error("Error listing survey responses:", error);
      return { success: false, message: error.message };
    }
  }

  // List all Firebase Auth users
  static async listAuthUsers() {
    try {
      const listAuthUsers = httpsCallable(functions, "listAuthUsers");
      const result = await listAuthUsers();
      return result.data;
    } catch (error) {
      console.error("Error listing auth users:", error);
      return { success: false, message: error.message };
    }
  }

  // Delete user completely from both Auth and Firestore
  static async deleteUserByAdmin(uid) {
    try {
      const deleteUserByAdmin = httpsCallable(functions, "deleteUserByAdmin");
      const result = await deleteUserByAdmin({ uid });
      return result.data;
    } catch (error) {
      console.error("Error deleting user:", error);
      return { success: false, message: error.message };
    }
  }

  // Delete user and all their data
  static async deleteUser(userId, userEmail) {
    try {
      // First, delete the user document from Firestore
      await deleteDoc(doc(db, "users", userId));

      // Note: Deleting from Firebase Auth requires the user to be signed in
      // Since we're deleting as an admin, we can't directly delete another user's auth account
      // The user will need to be deleted manually from Firebase Console or through Admin SDK
      // For now, we'll delete the Firestore data and return a message about the auth deletion

      return {
        success: true,
        message: "User data deleted successfully.",
      };
    } catch (error) {
      console.error("Error deleting user:", error);
      return { success: false, message: error.message };
    }
  }

  // Get user details by UID
  static async getUserDetails(uid) {
    try {
      const { doc, getDoc } = await import("firebase/firestore");
      const userDoc = await getDoc(doc(db, "users", uid));

      if (userDoc.exists()) {
        return {
          success: true,
          user: {
            uid: userDoc.id,
            ...userDoc.data(),
          },
        };
      } else {
        return { success: false, message: "User not found." };
      }
    } catch (error) {
      console.error("Error getting user details:", error);
      return { success: false, message: error.message };
    }
  }

  // Get survey responses for a user
  static async getUserSurveyResponses(uid) {
    try {
      const { collection, getDocs, query, where } = await import(
        "firebase/firestore"
      );
      const responsesRef = collection(db, "surveyResponses");
      const q = query(responsesRef, where("userId", "==", uid));
      const querySnapshot = await getDocs(q);

      const responses = [];
      querySnapshot.forEach((doc) => {
        responses.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return { success: true, responses };
    } catch (error) {
      console.error("Error getting user survey responses:", error);
      return { success: false, message: error.message };
    }
  }

  // Get game progress for a user
  static async getUserGameProgress(uid) {
    try {
      const { collection, getDocs, query, where } = await import(
        "firebase/firestore"
      );
      const progressRef = collection(db, "gameProgress");
      const q = query(progressRef, where("userId", "==", uid));
      const querySnapshot = await getDocs(q);

      const progress = [];
      querySnapshot.forEach((doc) => {
        progress.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return { success: true, progress };
    } catch (error) {
      console.error("Error getting user game progress:", error);
      return { success: false, message: error.message };
    }
  }

  // Get competency data for a user
  static async getUserCompetencyData(uid) {
    try {
      const { collection, getDocs, query, where } = await import(
        "firebase/firestore"
      );
      const competencyRef = collection(db, "competencies");
      const q = query(competencyRef, where("userId", "==", uid));
      const querySnapshot = await getDocs(q);

      const competencies = [];
      querySnapshot.forEach((doc) => {
        competencies.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return { success: true, competencies };
    } catch (error) {
      console.error("Error getting user competency data:", error);
      return { success: false, message: error.message };
    }
  }

  // Add new admin email
  static async addAdminEmail(email) {
    try {
      const addAdminEmail = httpsCallable(functions, "addAdminEmail");
      const result = await addAdminEmail({ email });
      if (result.data.success) {
        return {
          success: true,
          message: result.data.message,
        };
      } else {
        return { success: false, message: result.data.message };
      }
    } catch (error) {
      console.error("Error adding admin email:", error);
      return { success: false, message: error.message };
    }
  }

  // Remove admin email
  static async removeAdminEmail(email) {
    try {
      const removeAdminEmail = httpsCallable(functions, "removeAdminEmail");
      const result = await removeAdminEmail({ email });
      if (result.data.success) {
        return {
          success: true,
          message: result.data.message,
        };
      } else {
        return { success: false, message: result.data.message };
      }
    } catch (error) {
      console.error("Error removing admin email:", error);
      return { success: false, message: error.message };
    }
  }

  // Get list of admin emails
  static async getAdminEmails() {
    try {
      const getAdminEmails = httpsCallable(functions, "getAdminEmails");
      const result = await getAdminEmails();
      if (result.data.success) {
        return {
          success: true,
          adminEmails: result.data.adminEmails,
        };
      } else {
        return { success: false, message: result.data.message };
      }
    } catch (error) {
      console.error("Error getting admin emails:", error);
      return { success: false, message: error.message };
    }
  }
}
